/**
 * Settings → Organization. The legal identity Paddle prints on invoices.
 *
 * Every field here goes straight into a Paddle object at billing time:
 * name → business.name, country/street/city/postal → the address,
 * vat_id → business.tax_identifier, contact_email → customer.email,
 * which is the only address Paddle sends invoices to.
 *
 * The readiness line under the form is the server's verdict, not a
 * local guess — the same call the payment flow will make. Getting this
 * wrong is expensive in one direction only: country and postal code
 * cannot be corrected on an issued invoice by any means, so it is worth
 * refusing to bill rather than billing wrong.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import {
  useCheckVat,
  useBillingCountries,
  useOrgLegal,
  useUpdateOrgLegal,
  type BillingCountry,
  type OrgLegal,
} from "@/lib/api-hooks";
import { useAuth } from "@/lib/useAuth";
import { StSectionHead } from "./_shared";

type Draft = {
  name: string;
  legal_type: string;
  country: string;
  contact_email: string;
  street: string;
  city: string;
  postal_code: string;
  vat_id: string;
};

const EMPTY: Draft = {
  name: "",
  legal_type: "Company",
  country: "",
  contact_email: "",
  street: "",
  city: "",
  postal_code: "",
  vat_id: "",
};

/** Field key → what to call it when telling someone it is missing. */
const FIELD_LABELS: Record<string, string> = {
  name: "Organization name",
  legal_type: "Legal entity type",
  country: "Country",
  contact_email: "Email for invoices",
  street: "Address — street",
  city: "City",
  postal_code: "Postal code",
  vat_id: "VAT / Tax ID",
};

function label(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

function fromServer(d: OrgLegal): Draft {
  return {
    name: d.name ?? "",
    legal_type: d.legal_type ?? "",
    country: d.country ?? "",
    contact_email: d.contact_email ?? "",
    street: d.street ?? "",
    city: d.city ?? "",
    postal_code: d.postal_code ?? "",
    vat_id: d.vat_id ?? "",
  };
}

/**
 * Country picker with search. 229 options is far past what a plain
 * <select> is usable for, and the value stored is the ISO-2 code
 * because that is the only thing Paddle accepts.
 */
function CountrySelect({
  value,
  countries,
  disabled,
  onChange,
}: {
  value: string;
  countries: BillingCountry[];
  disabled: boolean;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = countries.find((c) => c.code === value) ?? null;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return countries;
    // Match the code too: someone who knows "GB" should not have to
    // remember whether we call it United Kingdom or Great Britain.
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) ||
        c.code.toLowerCase().startsWith(needle),
    );
  }, [countries, q]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  useLayer(open, () => setOpen(false));

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => setActive(0), [q]);

  const pick = (c: BillingCountry) => {
    onChange(c.code);
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          textAlign: "left",
          cursor: disabled ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span style={{ color: selected ? undefined : "var(--c-text-3)" }}>
          {selected ? `${selected.name} (${selected.code})` : "Select a country"}
        </span>
        <span style={{ opacity: 0.55, fontSize: 11 }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 60,
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "var(--c-surface)",
            border: "1px solid var(--c-border)",
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,.14)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 8, borderBottom: "1px solid var(--c-border)" }}>
            <input
              ref={inputRef}
              className="input"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((i) => Math.min(i + 1, shown.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const c = shown[active];
                  if (c) pick(c);
                }
              }}
            />
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {shown.length === 0 && (
              <div
                style={{
                  padding: "10px 12px",
                  color: "var(--c-text-3)",
                  fontSize: 13,
                }}
              >
                Nothing matches. Paddle does not process payments for every
                country.
              </div>
            )}
            {shown.map((c, i) => (
              <div
                key={c.code}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(c)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: 13,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  background:
                    i === active ? "var(--c-surface-alt)" : "transparent",
                  fontWeight: c.code === value ? 600 : 400,
                }}
              >
                <span>{c.name}</span>
                <span style={{ opacity: 0.5, fontVariantNumeric: "tabular-nums" }}>
                  {c.code}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** The server's answer to "can we bill this org yet", rendered plainly. */
function ReadinessNote({ data }: { data: OrgLegal }) {
  const missing = data.missing_fields ?? [];
  const recommended = data.recommended_fields ?? [];

  const tone = data.billing_ready ? "ok" : "warn";
  const border = tone === "ok" ? "var(--c-pos, #17864a)" : "var(--c-warn, #b26a00)";

  return (
    <div
      style={{
        marginTop: 16,
        padding: "12px 14px",
        border: `1px solid ${border}`,
        borderRadius: 10,
        background: "var(--c-surface-alt)",
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      {data.billing_ready ? (
        <div>
          <strong>Your details are enough to issue an invoice.</strong>{" "}
          {data.invoice_kind === "company"
            ? "It will be made out to your company."
            : "It will be made out to you as an individual."}
        </div>
      ) : (
        <div>
          <strong>Not enough yet to issue an invoice to your company.</strong>
          <div style={{ marginTop: 6 }}>
            Still needed:{" "}
            {missing.map((k, i) => (
              <span key={k}>
                {i > 0 && ", "}
                <b>{label(k)}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      {recommended.length > 0 && (
        <div style={{ marginTop: 8, color: "var(--c-text-3)" }}>
          Optional, but worth adding:{" "}
          {recommended.map((k, i) => (
            <span key={k}>
              {i > 0 && ", "}
              <b>{label(k)}</b>
            </span>
          ))}
          . Without a tax number your business cannot be zero-rated, so VAT
          is added on top and you carry it.
        </div>
      )}

      <div style={{ marginTop: 8, color: "var(--c-text-3)" }}>
        Country and postal code cannot be corrected on an invoice once it has
        been issued — only a new invoice can. Everything else can be updated
        later and the next invoice picks it up.
      </div>
    </div>
  );
}

export function OrganizationTab() {
  const { user } = useAuth();
  const orgId = user?.org_id ?? null;
  // Реквизиты — предмет финменеджера ровно так же, как админа: счета
  // печатаются по ним, и держать его в роли зрителя на собственном
  // рабочем экране значило бы отдать правку тому, кого она не касается.
  const canEdit =
    user?.role === "admin" || user?.role === "finance_manager";
  const query = useOrgLegal(orgId);
  const countriesQuery = useBillingCountries();
  const update = useUpdateOrgLegal();
  const checkVat = useCheckVat();

  const [editing, setEditing] = useState(false);
  const [v, setV] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setV(fromServer(query.data));
  }, [query.data]);

  const countries = countriesQuery.data ?? [];
  const country = countries.find((c) => c.code === v.country) ?? null;

  const set = <K extends keyof Draft>(k: K, val: Draft[K]) =>
    setV((prev) => ({ ...prev, [k]: val }));

  // Итог проверки налогового номера. Держится отдельно от ошибки
  // сохранения: это предупреждение, а не отказ, и путать их нельзя.
  const [vat, setVat] = useState<{
    status: "valid" | "invalid" | "unknown";
    text: string;
  } | null>(null);

  const save = async () => {
    if (!orgId) return;
    setError(null);
    try {
      await update.mutateAsync({
        orgId,
        patch: {
          name: v.name,
          legal_type: v.legal_type || null,
          country: v.country || null,
          contact_email: v.contact_email || null,
          street: v.street || null,
          city: v.city || null,
          postal_code: v.postal_code || null,
          vat_id: v.vat_id || null,
        },
      });
      setEditing(false);
      // ★ ПОСЛЕ сохранения и не мешая ему. Реквизиты уже записаны; ответ
      // VIES ничего не отменяет и ни к чему не обязывает — он лишь
      // предупреждает, что счёт выйдет с НДС, а не с обратным начислением.
      if (v.vat_id) {
        checkVat
          .mutateAsync({ orgId, vatId: v.vat_id, country: v.country })
          .then((r) => setVat({ status: r.status, text: r.text }))
          .catch(() => setVat(null));
      } else {
        setVat(null);
      }
    } catch (e) {
      // The server rejects on format, not on incompleteness — so this is
      // always something the person can fix in the field they just typed.
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Could not save. Please retry.";
      setError(msg);
    }
  };

  return (
    <>
      <StSectionHead
        title="Organization"
        desc="Legal data Paddle prints on your invoices. Fill it in once — every invoice after that is pre-filled, and you only enter a card."
        right={
          canEdit ? (
            editing ? (
              <div style={{ display: "flex", gap: 8 }}>
                <Btn
                  onClick={() => {
                    if (query.data) setV(fromServer(query.data));
                    setError(null);
                    setEditing(false);
                  }}
                >
                  Cancel
                </Btn>
                <Btn
                  variant="primary"
                  icon={I.check}
                  onClick={() => void save()}
                  disabled={update.isPending}
                >
                  Save
                </Btn>
              </div>
            ) : (
              <Btn icon={I.edit} onClick={() => setEditing(true)}>
                Edit
              </Btn>
            )
          ) : undefined
        }
      />
      <div className="card card-pad" style={{ maxWidth: 780 }}>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
        >
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">Organization name</label>
            <input
              className="input"
              disabled={!editing}
              value={v.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label">Legal entity type</label>
            <select
              className="select"
              disabled={!editing}
              value={v.legal_type}
              onChange={(e) => set("legal_type", e.target.value)}
            >
              <option value="">—</option>
              <option value="Company">Company</option>
              <option value="Individual">Individual</option>
            </select>
          </div>

          <div className="field">
            <label className="label">Country</label>
            <CountrySelect
              value={v.country}
              countries={countries}
              disabled={!editing}
              onChange={(code) => set("country", code)}
            />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">Email for invoices</label>
            <input
              className="input"
              disabled={!editing}
              value={v.contact_email}
              onChange={(e) => set("contact_email", e.target.value)}
            />
            <div className="hint">
              Paddle sends every invoice here, and nowhere else.
            </div>
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">Address — street</label>
            <input
              className="input"
              disabled={!editing}
              value={v.street}
              onChange={(e) => set("street", e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label">City</label>
            <input
              className="input"
              disabled={!editing}
              value={v.city}
              onChange={(e) => set("city", e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label">
              Postal code
              {country?.postal_code_required ? (
                <span style={{ color: "var(--c-warn, #b26a00)" }}>
                  {" "}
                  — required for {country.name}
                </span>
              ) : null}
            </label>
            <input
              className="input"
              disabled={!editing}
              value={v.postal_code}
              onChange={(e) => set("postal_code", e.target.value)}
            />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">VAT / Tax ID</label>
            <input
              className="input mono"
              disabled={!editing}
              value={v.vat_id}
              onChange={(e) => set("vat_id", e.target.value)}
            />
            <div className="hint">
              {country?.vat_example
                ? `Format for ${country.name}: ${country.vat_example}`
                : "Optional. With it, cross-border business sales are zero-rated; without it VAT is added on top."}
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              border: "1px solid var(--c-neg, #b3261e)",
              borderRadius: 10,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* ★ Предупреждение, а не отказ. Реквизиты уже сохранены; неверный
            номер означает лишь счёт с НДС вместо обратного начисления, и
            это исправимо. Отдельно — «не удалось проверить»: VIES лежит
            регулярно, и сказать из-за его аварии, что у компании плохой
            номер, было бы неправдой. */}
        {vat && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 10,
              fontSize: 13,
              border:
                vat.status === "invalid"
                  ? "1px solid var(--c-warn-600, #8a5a04)"
                  : "1px solid var(--c-divider)",
              background:
                vat.status === "invalid"
                  ? "var(--c-warn-50, #fdf6e6)"
                  : "var(--c-surface-sunk)",
              color:
                vat.status === "valid"
                  ? "var(--c-text-2)"
                  : "var(--c-text)",
            }}
          >
            {vat.text}
          </div>
        )}

        {query.data && <ReadinessNote data={query.data} />}
      </div>
    </>
  );
}
